/**
 * "Does the plan selection step actually have anything to show?"
 *
 * Used to decide whether to fire the no-plans report when step 2 is reached.
 * A top-level `length > 0` is not enough: the API can return packages whose
 * groups are empty, which renders an empty plans screen even though the
 * packages array looked populated.
 *
 * The shape differs per form, so this walks all of them:
 *
 *   GIG      packages: [ { groups: { '0': [ { plans: [...] } ] } } ]
 *   CORP     blocks:   { paidByCompany:    [ { groupsCP: [ { plans: [...] } ] } ],
 *                        coveredByCompany: [ { groupsCC: [...] } ],
 *                        paidByEmployee:   [ { groupsEP: [...] } ] }
 *
 * `groups` is sometimes an object keyed by index and sometimes a plain array,
 * so both are handled. Anything unrecognised counts as zero rather than
 * throwing — a reporting call must never break the plans screen.
 */

/** Group buckets a package can carry, across the GIG and CORP shapes. */
const GROUP_KEYS = ['groups', 'groupsCP', 'groupsCC', 'groupsEP', 'groupsOptional'];

/** Object or array → array of values. Anything else → empty. */
function toList(value: any): any[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'object') return Object.values(value);
  return [];
}

/** Total number of plans across every package/group in `packages`. */
export function countAvailablePlans(packages: any): number {
  let count = 0;

  for (const entry of toList(packages)) {
    if (!entry) continue;

    // A blocks value is itself a list of packages (blocks.paidByCompany = [...]).
    if (Array.isArray(entry)) {
      count += countAvailablePlans(entry);
      continue;
    }
    if (typeof entry !== 'object') continue;

    for (const groupKey of GROUP_KEYS) {
      for (const group of toList(entry[groupKey])) {
        // groups can nest one level deeper when the bucket is keyed by index.
        if (Array.isArray(group)) {
          for (const inner of group) count += toList(inner?.plans).length;
        } else {
          count += toList(group?.plans).length;
        }
      }
    }
  }

  return count;
}

/** True when the plans screen has at least one plan to render. */
export function hasAvailablePlans(packages: any): boolean {
  return countAvailablePlans(packages) > 0;
}
